/**
 * Import via Account Aggregator Button Component
 *
 * A button that initiates the Account Aggregator consent flow by:
 * 1. Calling BFF /aa/redirect endpoint to get the Saafe redirect URL
 * 2. Redirecting user to Saafe for consent flow
 *
 * Features:
 * - Loading state during API call
 * - Error handling with toast notifications
 * - AA logo icon with customizable appearance
 * - Consistent with Compass design system
 */

import * as React from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button, type buttonVariants } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import type { VariantProps } from "class-variance-authority";
import {
  getAARedirectUrl,
  getAAErrorMessage,
  type AARedirectOptions,
} from "@/lib/aa-api";

// ============================================================================
// Types
// ============================================================================

export interface ImportViaAAButtonProps
  extends Omit<React.ComponentProps<"button">, "onClick">,
    VariantProps<typeof buttonVariants> {
  /** Optional label text (default: "Import via Account Aggregator") */
  label?: string;
  /** Whether to show the AA icon */
  showIcon?: boolean;
  /** Return URL after consent flow (default: current page) */
  returnUrl?: string;
  /** Financial information types to request (default: MUTUAL_FUNDS) */
  fiTypes?: string[];
  /** Callback fired when redirect is initiated successfully */
  onRedirectStart?: (redirectUrl: string) => void;
  /** Callback fired on redirect error */
  onRedirectError?: (error: Error) => void;
}

// ============================================================================
// AA Icon Component
// ============================================================================

/**
 * Account Aggregator Network Icon
 *
 * A stylized network/nodes icon representing the Account Aggregator ecosystem.
 * Based on the DEPA (Data Empowerment and Protection Architecture) visual language.
 */
function AAIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
    >
      {/* Central hub - represents the Account Aggregator */}
      <circle cx="12" cy="12" r="3" fill="currentColor" strokeWidth="0" />

      {/* Connection nodes - represent FIPs (Financial Information Providers) */}
      <circle cx="12" cy="4" r="2" />
      <circle cx="19" cy="8" r="2" />
      <circle cx="19" cy="16" r="2" />
      <circle cx="12" cy="20" r="2" />
      <circle cx="5" cy="16" r="2" />
      <circle cx="5" cy="8" r="2" />

      {/* Connection lines from center to nodes */}
      <line x1="12" y1="9" x2="12" y2="6" />
      <line x1="14.5" y1="10" x2="17" y2="8" />
      <line x1="14.5" y1="14" x2="17" y2="16" />
      <line x1="12" y1="15" x2="12" y2="18" />
      <line x1="9.5" y1="14" x2="7" y2="16" />
      <line x1="9.5" y1="10" x2="7" y2="8" />
    </svg>
  );
}

// ============================================================================
// Constants
// ============================================================================

const DEFAULT_LABEL = "Import via Account Aggregator";

// ============================================================================
// Main Component
// ============================================================================

/**
 * ImportViaAAButton
 *
 * A button that initiates the Account Aggregator consent flow.
 * Calls the BFF to get a redirect URL, then navigates to Saafe for consent.
 *
 * @example
 * ```tsx
 * // Basic usage
 * <ImportViaAAButton />
 *
 * // With custom label and styling
 * <ImportViaAAButton
 *   label="Link Accounts"
 *   variant="default"
 *   size="lg"
 * />
 *
 * // With custom return URL
 * <ImportViaAAButton
 *   returnUrl="/portfolio"
 *   onRedirectStart={(url) => console.log('Redirecting to:', url)}
 * />
 *
 * // Request multiple FI types
 * <ImportViaAAButton
 *   fiTypes={['MUTUAL_FUNDS', 'EQUITIES']}
 * />
 * ```
 */
export function ImportViaAAButton({
  label = DEFAULT_LABEL,
  showIcon = true,
  returnUrl,
  fiTypes,
  variant = "default",
  size = "default",
  className,
  onRedirectStart,
  onRedirectError,
  disabled,
  ...props
}: ImportViaAAButtonProps) {
  const [isLoading, setIsLoading] = React.useState(false);
  const safetyTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(
    null
  );

  // Cleanup safety timeout on unmount
  React.useEffect(() => {
    return () => {
      if (safetyTimeoutRef.current) {
        clearTimeout(safetyTimeoutRef.current);
      }
    };
  }, []);

  const handleClick = React.useCallback(
    async (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      e.stopPropagation();

      if (isLoading) return;

      setIsLoading(true);

      try {
        const options: AARedirectOptions = {};

        if (returnUrl) {
          options.returnUrl = returnUrl;
        }

        if (fiTypes && fiTypes.length > 0) {
          options.fiTypes = fiTypes;
        }

        const { redirectUrl } = await getAARedirectUrl(options);

        onRedirectStart?.(redirectUrl);

        // Redirect to Saafe for consent flow
        window.location.href = redirectUrl;

        // Safety timeout: Reset loading state if redirect fails silently
        // (e.g., browser blocks it, network issue, or popup blocker)
        safetyTimeoutRef.current = setTimeout(() => {
          setIsLoading(false);
        }, 5000);
      } catch (error) {
        const err =
          error instanceof Error
            ? error
            : new Error("Failed to initiate Account Aggregator flow");

        const errorMessage = getAAErrorMessage(error);

        toast.error("Failed to start import", {
          description: errorMessage,
          duration: 5000,
        });

        onRedirectError?.(err);
        setIsLoading(false);
      }
      // Note: On success, we set a safety timeout to reset isLoading after 5s
      // in case the redirect fails silently (browser blocks, network issue, etc.)
    },
    [isLoading, returnUrl, fiTypes, onRedirectStart, onRedirectError]
  );

  return (
    <Button
      type="button"
      variant={variant}
      size={size}
      className={cn("gap-2", className)}
      onClick={handleClick}
      disabled={disabled || isLoading}
      aria-label={label}
      {...props}
    >
      {isLoading ? (
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
      ) : showIcon ? (
        <AAIcon className="h-4 w-4" />
      ) : null}
      <span>{isLoading ? "Connecting..." : label}</span>
    </Button>
  );
}

// ============================================================================
// Variants
// ============================================================================

/**
 * Compact AA import button with shorter label
 */
export function ImportViaAAButtonCompact({
  className,
  ...props
}: Omit<ImportViaAAButtonProps, "label">) {
  return (
    <ImportViaAAButton
      {...props}
      label="Import via AA"
      size="sm"
      className={className}
    />
  );
}

/**
 * AA import button styled as secondary/outline for less prominent placements
 */
export function ImportViaAAButtonSecondary({
  className,
  ...props
}: Omit<ImportViaAAButtonProps, "variant">) {
  return (
    <ImportViaAAButton
      {...props}
      variant="outline"
      className={className}
    />
  );
}

/**
 * Icon-only AA import button for very compact spaces
 * Note: This extends ImportViaAAButton and includes the full redirect functionality.
 * Use this when you need a minimal icon button that still triggers the AA consent flow.
 */
export function ImportViaAAButtonIcon({
  className,
  variant = "ghost",
  ...props
}: Omit<ImportViaAAButtonProps, "label" | "showIcon" | "size"> & {
  /** Click handler for the icon button */
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
}) {
  return (
    <ImportViaAAButton
      {...props}
      variant={variant}
      size="icon"
      label=""
      showIcon
      className={cn("", className)}
    />
  );
}

// ============================================================================
// Exported Icon
// ============================================================================

export { AAIcon };

export default ImportViaAAButton;
